/* eslint-disable no-bitwise */
import type { EncodedAudioData } from '../mpegts/demuxerTypes';
import { BitWriter } from '../flac/bitWriter';
import { invariant } from '../util/invariant';
import { BitReader } from './bitReader';
import { getChannelLayoutMapping } from './channelLayout';
import { decodeScaleFactor, decodeSpectralData } from './codebooks';
import {
  ID_END, ID_SCE, ID_CPE, ID_CCE, ID_LFE, ID_DSE, ID_PCE, ID_FIL, EXT_DYNAMIC_RANGE, EXT_FILL_DATA,
  EXT_SBR_DATA, EXT_SBR_DATA_CRC, INTENSITY_HCB, INTENSITY_HCB2, NOISE_BAND_TYPE, EIGHT_SHORT_SEQUENCE,
  inverseQuantizationLookup, NOISE_PRE, NOISE_PRE_BITS, scaleFactorLookup, SF_OFFSET, tns_tmp2_map, ZERO_HCB,
  LONG_START_SEQUENCE, LONG_STOP_SEQUENCE, num_swb_long_window, num_swb_short_window, ONLY_LONG_SEQUENCE,
  PRED_SFB_MAX, swb_offset_long_window, swb_offset_short_window, tns_max_bands_1024, tns_max_bands_128,
} from './data';
import { MDCT } from './mdct';
import { convertSpectralValuesToSamples, convertFloatPcmToS16LE } from './spectralToSamples';
import { type SingleChannelElement, type ChannelPairElement, type CouplingChannelElement, type LfeChannelElement, type DataStreamElement, type ProgramConfigElement, type ChannelElement, type DynamicRangeInfo, type ExcludeChannelsInfo, type IcsInfo, type SbrData, type SbrExtensionData, type SbrHeader, type ScalefactorBandsAndGroupingInfo, MAX_SCALE_FACTORS, type Pulse, type SectionDataInfo, type TemporalNoiseShaping, TNS_MAX_ORDER } from './types';
import type { DecodedPcmFloat32Channels } from './types';


/**
 * This class implements a decoder for AAC Low Complexity that,
 * given AAC frames, will provide the corresponding raw PCM S16LE
 * samples.
 *
 *
 * AAC specifications:
 * https://drive.google.com/file/d/1Q9U60PIHYWb8rY4ImQWBikQG5Kenr9T9/view?usp=sharing
 *
 *
 *
 * ---------------------
 * AAC decoding overview
 * ---------------------
 *
 *
 * An AAC frame contains a sequence of elements that describe 1024 audio samples.
 * These elements can be:
 * - SCE (single channel element): describe a single audio channel
 * - CPE (channel pair element): describe two audio channels
 * - LFE (low frequency channel element): describe a single audio channek with low sampling frequency enhancement
 * - CCE (coupling channel element): not sure what it is, since it seems that no AAC encoder produces these elements
 * - PCE (program config element): used to describe a non standard audio channel layout
 * - DSE (data stream element): used for metadata
 * - FIL (filling element): used for padding or for storing extra information in the SSR AAC profile
 *
 * For each audio channel element (SCE, CPE, LFE or CCE), we need to decode an array of spectral coefficients (or 2
 * arrays for CPE since it has two channels). These coefficients are encoded with a Huffman prefix code.
 *
 * Once all the coefficients have been decoded, they are converted to actual PCM samples by doing the following:
 * - apply temporal noise shaping if necessary
 * - apply MDCT (modified discrete cosine transform); this step is the reverse of the quantization process used in encoding
 * - apply windowing with the previous decoded samples
 * - convert floating point values between -1.0 and 1.0 to signed 16-bits integers
 *
 *
 * Since decoding the AAC packet #n depending on having decoded the
 * packet #(n-1) just before, the caller is responsible for
 * decoding ts segments in the correct order. If we want to decode segment #n
 * and the last decoded segment is not #(n-1), the caller is responsible for
 * first decoding the last AAC frame of segment #(n-1).
 */
export class AacDecoder {

  private bitReader: BitReader;
  private current_window_shape: number[] = [];
  private current_window_sequence: number[] = [];
  private currentPce: ProgramConfigElement | undefined;

  // In order to avoid audio artefacts, AAC takes the previous window N-1
  // into account when processing the window N. This is why we need to remember
  // the shape of the previous window and to keep information about overlapping
  // data between the windows N-1 and N
  private previous_window_shape: number[] = [];
  private previous_window_sequence: number[] = [];
  private overlaps: Float32Array[] = [];
  private mdctLong = new MDCT(2048);
  private mdctShort = new MDCT(256);

  private randomState = 0x1F2E3D4C;

  // Re-usable buffers to improve performance,
  // one buffer per channel
  private coeffs: Float32Array[] = [];
  private scaleFactors: Float32Array[] = [];
  private samples: Float32Array[] = [];

  private tns_tmp = new Float32Array(TNS_MAX_ORDER + 1);
  private tns_lpc = new Float32Array(TNS_MAX_ORDER);
  private imdct_and_window_buf = new Float32Array(1024);
  private imdct_and_window_tmp = new Float32Array(1024);

  private numberOfChannels: number | undefined;

  // When decoding channels as float samples, for each channel, we have an
  // array of N Float32Array where N is the number of AAC frames that have
  // been decoded
  private channels: Float32Array[][] = [];
  private bitWriter = new BitWriter();


  constructor(
    private sampling_frequency_index: number,
    private channel_configuration: number,
    private emitPCMs16LESamples: boolean,
  ) {
    this.bitReader = new BitReader(new Uint8Array(0));
  }

  /**
   * Decodes one AAC frame from the given bitstream and returns the number of audio channels.
   */
  decodeAacFrame(bitReader: BitReader, onlyProbe: boolean): number {
    if (this.bitReader !== bitReader) {
      invariant(!this.bitReader.hasMoreData(), 'Should not have data in bit reader');
      this.bitReader = bitReader;
    }

    return this.raw_data_block(this.sampling_frequency_index, this.channel_configuration, onlyProbe);
  }


  decodeAacFrameFromArray(bytes: Uint8Array): number {
    this.bitReader.setNewData(bytes);
    return this.decodeAacFrame(this.bitReader, false);
  }


  /**
   * Decodes all the given audio data.
   */
  decodeAacFrames(encodedData: EncodedAudioData): DecodedPcmFloat32Channels {
    this.clearPcmSamples(this.emitPCMs16LESamples);
    for (const aacFrame of encodedData.frames) {
      this.decodeAacFrameFromArray(aacFrame.data);
    }

    const channels: Float32Array[] = [];
    for (const channel of this.channels) {
      let nSamples = 0;
      for (const sampleArray of channel) {
        nSamples += sampleArray.length;
      }
      const allSamples = new Float32Array(nSamples);
      let offset = 0;
      for (const sampleArray of channel) {
        allSamples.set(sampleArray, offset);
        offset += sampleArray.length;
      }

      channels.push(allSamples);
    }
    return {
      sampleRateInHz: encodedData.sampleRateInHz,
      channels,
    };
  }


  private raw_data_block(sampling_frequency_index: number, channel_configuration: number, onlyProbe: boolean): number {
    let id;

    const sceElements: SingleChannelElement[] = [];
    const cpeElements: ChannelPairElement[] = [];
    const cceElements: CouplingChannelElement[] = [];
    const lfeElements: LfeChannelElement[] = [];
    const dseElements: DataStreamElement[] = [];

    let pceElement: ProgramConfigElement | undefined;
    let channelIndex = 0;
    while ((id = this.bitReader.readBits(3)) !== ID_END) {
      switch (id) {
        case ID_SCE: {
          sceElements.push(this.single_channel_element(sampling_frequency_index, channelIndex));
          channelIndex += 1;
          break;
        }
        case ID_CPE: {
          cpeElements.push(this.channel_pair_element(sampling_frequency_index, channelIndex));
          channelIndex += 2;
          break;
        }
        case ID_CCE: {
          cceElements.push(this.coupling_channel_element());
          break;
        }
        case ID_LFE: {
          lfeElements.push(this.lfe_channel_element(sampling_frequency_index, channelIndex));
          channelIndex += 1;
          break;
        }
        case ID_DSE: {
          dseElements.push(this.data_stream_element());
          break;
        }
        case ID_PCE: {
          invariant(pceElement === undefined, 'Should not have more than one PCE');
          pceElement = this.program_config_element();
          this.currentPce = pceElement;
          break;
        }
        case ID_FIL: {
          this.fill_element();
          break;
        }
      }
    }

    const channelMapping = getChannelLayoutMapping(channel_configuration, this.currentPce, sceElements, cpeElements, lfeElements);
    if (onlyProbe) {
      return channelMapping.length;
    }

    this.bitReader.alignByte();

    // There is a theoretical possibility of having only 960 samples instead of 1024,
    // but it seems to be used only for DAB+ so let's ignore that for now
    const samples = 1024;
    const channelElements = [...sceElements, ...cpeElements, ...lfeElements, ...cceElements];

    convertSpectralValuesToSamples(channelElements, this.mdctLong, this.mdctShort, this.tns_tmp, this.tns_lpc, this.imdct_and_window_buf, this.imdct_and_window_tmp);

    if (this.numberOfChannels === undefined) {
      this.numberOfChannels = channelIndex;
    } else if (this.numberOfChannels !== channelIndex) {
      throw new Error(`Inconsistent number of channels: previous = ${this.numberOfChannels}, current = ${channelIndex}`);
    }


    if (this.emitPCMs16LESamples) {
      for (let i = 0; i < samples; i++) {
        for (let ch = 0; ch < this.numberOfChannels; ch++) {
          const mappedChannel = channelMapping[ch];
          if (mappedChannel === undefined) continue;
          const sample = convertFloatPcmToS16LE(this.samples[mappedChannel]![i]!);
          this.bitWriter.write16LE(sample);
        }
      }
    } else {
      for (let ch = 0; ch < this.numberOfChannels; ch++) {
        if (this.channels[ch] === undefined) {
          this.channels[ch] = [];
        }
        const mappedChannel = channelMapping[ch];
        if (mappedChannel === undefined) continue;
        const copy = new Float32Array(this.samples[mappedChannel]!);
        this.channels[ch]!.push(copy);
      }
    }

    return channelMapping.length;
  }


  private single_channel_element(sampling_frequency_index: number, channelIndex: number): SingleChannelElement {
    const element_instance_tag = this.bitReader.readBits(4);
    const channel0 = this.individual_channel_stream(0, sampling_frequency_index, undefined, undefined, channelIndex);
    return {
      element_instance_tag,
      type: 'SCE',
      channel0,
    };
  }


  private channel_pair_element(sampling_frequency_index: number, channelIndex: number): ChannelPairElement {
    const element_instance_tag = this.bitReader.readBits(4);
    const common_window = this.bitReader.readBits(1);

    const ms_mask: number[] = [];
    let ics_info: IcsInfo | undefined;
    let scalefactorBandsAndGroupingInfo: ScalefactorBandsAndGroupingInfo | undefined;

    let ms_mask_present = 0;

    if (common_window) {
      ics_info = this.ics_info(sampling_frequency_index, channelIndex);
      // Since we have a common window, let's copy the channel info to the
      // second channel of this pair
      this.previous_window_sequence[channelIndex + 1] = this.previous_window_sequence[channelIndex]!;
      this.current_window_sequence[channelIndex + 1] = this.current_window_sequence[channelIndex]!;
      this.previous_window_shape[channelIndex + 1] = this.previous_window_shape[channelIndex]!;
      this.current_window_shape[channelIndex + 1] = this.current_window_shape[channelIndex]!;

      scalefactorBandsAndGroupingInfo = this.getScalefactorBandsAndGroupingInfo(sampling_frequency_index, ics_info);
      ms_mask_present = this.bitReader.readBits(2);
      const num_window_groups = scalefactorBandsAndGroupingInfo.num_window_groups;
      const max_sfb = ics_info.max_sfb;
      const maxIdx = num_window_groups * max_sfb;
      switch (ms_mask_present) {
        case 0: {
          // All 0s
          for (let idx = 0; idx < maxIdx; idx++) {
            ms_mask[idx] = 0;
          }
          break;
        }
        case 1: {
          // Read from bitstream
          for (let idx = 0; idx < maxIdx; idx++) {
            ms_mask[idx] = this.bitReader.readBits(1);
          }
          break;
        }
        case 2: {
          // All 1s
          for (let idx = 0; idx < maxIdx; idx++) {
            ms_mask[idx] = 1;
          }
          break;
        }
        case 3: {
          throw new Error('ms_mask_present 3 is reserved !');
        }
      }
    }

    const channel0 = this.individual_channel_stream(common_window, sampling_frequency_index, ics_info, scalefactorBandsAndGroupingInfo, channelIndex);
    const channel1 = this.individual_channel_stream(common_window, sampling_frequency_index, ics_info, scalefactorBandsAndGroupingInfo, channelIndex + 1);

    if (common_window) {
      if (ms_mask_present !== 0) {
        this.apply_mid_side_stereo(channel0, channel1, ms_mask);
      }
    }

    this.apply_intensity_stereo(channel0, channel1, ms_mask_present !== 0 ? ms_mask : undefined);

    return {
      element_instance_tag,
      type: 'CPE',
      channel0,
      channel1,
    };
  }


  private apply_intensity_stereo(channel0: ChannelElement, channel1: ChannelElement, ms_mask: number[] | undefined) {
    let offsetChannels = 0;
    const offsets = channel1.scalefactorBandsAndGroupingInfo.swb_offset;
    let idx = 0;
    for (let g = 0; g < channel1.scalefactorBandsAndGroupingInfo.num_window_groups; g++) {
      for (let i = 0; i < channel1.ics_info.max_sfb;) {
        const bt_run_end = channel1.section_data_info.band_type_run_end[idx]!;
        if (channel1.section_data_info.band_type[idx] === INTENSITY_HCB || channel1.section_data_info.band_type[idx] === INTENSITY_HCB2) {
          for (; i < bt_run_end; i++, idx++) {
            let c = -1 + 2 * (channel1.section_data_info.band_type[idx]! - 14);
            if (ms_mask !== undefined) {
              c *= 1 - 2 * ms_mask[idx]!;
            }
            const scale = c * channel1.scaleFactors[idx]!;
            for (let group = 0; group < channel1.scalefactorBandsAndGroupingInfo.window_group_length[g]!; group++) {
              const length = offsets[i + 1]! - offsets[i]!;
              const startIndex = group * 128 + offsets[i]! + offsetChannels;

              for (let z = startIndex; z < startIndex + length; z++) {
                channel1.coeffs[z] = channel0.coeffs[z]! * scale;
              }
            }
          }
        } else {
          idx += bt_run_end - i;
          i = bt_run_end;
        }
      }
      offsetChannels += channel1.scalefactorBandsAndGroupingInfo.window_group_length[g]! * 128;
    }
  }


  private apply_mid_side_stereo(channel0: ChannelElement, channel1: ChannelElement, ms_mask: number[]) {
    let offsetChannels = 0;
    const offsets = channel0.scalefactorBandsAndGroupingInfo.swb_offset;
    let idx = 0;
    for (let g = 0; g < channel0.scalefactorBandsAndGroupingInfo.num_window_groups; g++) {
      for (let i = 0; i < channel0.ics_info.max_sfb; i++, idx++) {
        if (ms_mask[idx] && channel0.section_data_info.band_type[idx]! < NOISE_BAND_TYPE && channel1.section_data_info.band_type[idx]! < NOISE_BAND_TYPE) {
          for (let group = 0; group < channel0.scalefactorBandsAndGroupingInfo.window_group_length[g]!; group++) {
            const length = offsets[i + 1]! - offsets[i]!;
            const startIndex = group * 128 + offsets[i]! + offsetChannels;

            for (let z = startIndex; z < startIndex + length; z++) {
              const sum = channel0.coeffs[z]! + channel1.coeffs[z]!;
              const difference = channel0.coeffs[z]! - channel1.coeffs[z]!;
              channel0.coeffs[z] = sum;
              channel1.coeffs[z] = difference;
            }
          }
        }
      }
      offsetChannels += channel0.scalefactorBandsAndGroupingInfo.window_group_length[g]! * 128;
    }
  }


  private coupling_channel_element(): CouplingChannelElement {
    // Even though ffmpeg and libfdkaac support decoding CCEs, neither of them support
    // encoding them as there is no code to produce such elements in their codebases.
    //
    // Furthermore, none of the test files from https://samples.ffmpeg.org/A-codecs/AAC/
    // contain any CCE elements.
    //
    // Also, according to this post (https://hydrogenaud.io/index.php/topic,66785.msg597210.html#msg597210),
    // it seems that the reference for CCEs was a bit shaky:
    //
    //   "From a practical perspective CCE's are almost never implemented, the description in
    //   14496-3:2005 is inconsistent with the behavior of the reference decoder in several key
    //   ways and this went unnoticed for many years."
    //
    // As a consequence, let's ignore it for now. If a file using CCEs ever
    // crosses our path, we can implement support for it then.
    throw new Error('coupling_channel_element not implemented');
  }


  private lfe_channel_element(sampling_frequency_index: number, channelIndex: number): LfeChannelElement {
    const element_instance_tag = this.bitReader.readBits(4);
    const channel0 = this.individual_channel_stream(0, sampling_frequency_index, undefined, undefined, channelIndex);

    return {
      element_instance_tag,
      type: 'LFE',
      channel0,
    };
  }


  private data_stream_element(): DataStreamElement {
    throw new Error('data_stream_element not implemented');
  }


  private program_config_element(): ProgramConfigElement {
    const element_instance_tag = this.bitReader.readBits(4);

    const profile = this.bitReader.readBits(2);
    const sampling_frequency_index = this.bitReader.readBits(4);

    const num_front_channel_elements = this.bitReader.readBits(4);
    const num_side_channel_elements = this.bitReader.readBits(4);
    const num_back_channel_elements = this.bitReader.readBits(4);
    const num_lfe_channel_elements = this.bitReader.readBits(2);
    const num_assoc_data_elements = this.bitReader.readBits(3);
    const num_valid_cc_elements = this.bitReader.readBits(4);

    let mono_mixdown_element_number: number | undefined;
    const mono_mixdown_present = this.bitReader.readBits(1);
    if (mono_mixdown_present === 1) {
      mono_mixdown_element_number = this.bitReader.readBits(4);
    }

    let stereo_mixdown_element_number: number | undefined;
    const stereo_mixdown_present = this.bitReader.readBits(1);
    if (stereo_mixdown_present === 1) {
      stereo_mixdown_element_number = this.bitReader.readBits(4);
    }

    let matrix_mixdown_idx: number | undefined;
    let pseudo_surround_enable: boolean | undefined;
    const matrix_mixdown_idx_present = this.bitReader.readBits(1);
    if (matrix_mixdown_idx_present === 1) {
      matrix_mixdown_idx = this.bitReader.readBits(2);
      pseudo_surround_enable = (1 === this.bitReader.readBits(1));
    }


    const front_element_is_cpe: boolean[] = [];
    const front_element_tag_select: number[] = [];
    for (let i = 0; i < num_front_channel_elements; i++) {
      front_element_is_cpe[i] = (1 === this.bitReader.readBits(1));
      front_element_tag_select[i] = this.bitReader.readBits(4);
    }

    const side_element_is_cpe: boolean[] = [];
    const side_element_tag_select: number[] = [];
    for (let i = 0; i < num_side_channel_elements; i++) {
      side_element_is_cpe[i] = (1 === this.bitReader.readBits(1));
      side_element_tag_select[i] = this.bitReader.readBits(4);
    }

    const back_element_is_cpe: boolean[] = [];
    const back_element_tag_select: number[] = [];
    for (let i = 0; i < num_back_channel_elements; i++) {
      back_element_is_cpe[i] = (1 === this.bitReader.readBits(1));
      back_element_tag_select[i] = this.bitReader.readBits(4);
    }

    const lfe_element_tag_select: number[] = [];
    for (let i = 0; i < num_lfe_channel_elements; i++) {
      lfe_element_tag_select[i] = this.bitReader.readBits(4);
    }

    const assoc_data_element_tag_select: number[] = [];
    for (let i = 0; i < num_assoc_data_elements; i++) {
      assoc_data_element_tag_select[i] = this.bitReader.readBits(4);
    }

    const cc_element_is_ind_sw: boolean[] = [];
    const valid_cc_element_tag_select: number[] = [];
    for (let i = 0; i < num_valid_cc_elements; i++) {
      cc_element_is_ind_sw[i] = (1 === this.bitReader.readBits(1));
      valid_cc_element_tag_select[i] = this.bitReader.readBits(4);
    }

    this.bitReader.alignByte();

    const comment_field_bytes = this.bitReader.readBits(8);
    const comment_field_data: number[] = [];
    for (let i = 0; i < comment_field_bytes; i++) {
      comment_field_data[i] = this.bitReader.readBits(8);
    }

    return {
      element_instance_tag,
      type: 'PCE',
      profile,
      sampling_frequency_index,
      mono_mixdown_element_number,
      stereo_mixdown_element_number,
      matrix_mixdown_idx,
      pseudo_surround_enable,
      front_element_is_cpe,
      front_element_tag_select,
      side_element_is_cpe,
      side_element_tag_select,
      back_element_is_cpe,
      back_element_tag_select,
      lfe_element_tag_select,
      assoc_data_element_tag_select,
      cc_element_is_ind_sw,
      valid_cc_element_tag_select,
      comment_field_data,
    };
  }


  private fill_element() {
    let cnt = this.bitReader.readBits(4);
    if (cnt === 15) {
      cnt += this.bitReader.readBits(8) - 1;
    }

    while (cnt > 0) {
      const nBytesRead = this.extension_payload(cnt, undefined);
      cnt -= nBytesRead;
    }
  }


  private extension_payload(cnt: number, id_aac: number | undefined): number {
    const extension_type = this.bitReader.readBits(4);
    switch (extension_type) {
      case EXT_DYNAMIC_RANGE: return this.dynamic_range_info().n;
      case EXT_SBR_DATA: {
        if (id_aac === undefined) {
          throw new Error('HE-AAC is not supported');
        }
        return this.sbr_extension_data(cnt, id_aac, 0).n;
      }
      case EXT_SBR_DATA_CRC: {
        invariant(id_aac !== undefined, 'id_aac should be defined to decode EXT_SBR_DATA_CRC');
        return this.sbr_extension_data(cnt, id_aac, 1).n;
      }
      case EXT_FILL_DATA: {
        const fill_nibble = this.bitReader.readBits(4);
        if (fill_nibble !== 0) {
          throw new Error(`Invalid fill_nibble: ${fill_nibble}`);
        }
        for (let i = 0; i < cnt - 1; i++) {
          const fill_byte = this.bitReader.readBits(8);
          if (fill_byte !== 0b10100101) {
            throw new Error(`Invalid fill_byte: ${fill_byte}`);
          }
        }
        return cnt;
      }
      default: {
        for (let i = 0; i < 8 * (cnt - 1) + 4; i++) {
          this.bitReader.readBits(1);
        }
        return cnt;
      }
    }
  }


  private sbr_extension_data(cnt: number, id_aac: number, crc_flag: number): SbrExtensionData {
    let num_sbr_bits = 0;
    let bs_sbr_crc_bits = 0;

    if (crc_flag === 1) {
      bs_sbr_crc_bits = this.bitReader.readBits(10);
      num_sbr_bits += 10;
    }

    // ISO/IEC 14496-3 specifies that we should read the SRB header
    // only if sbr_layer != SBR_STEREO_ENHANCE which is the case
    // for a non-scalable AAC core coder. Since ffmpeg does not check
    // this, let's ignore it as well

    num_sbr_bits += 1;
    const bs_header_flag = this.bitReader.readBits(1);

    let sbrHeader: SbrHeader | undefined;
    if (bs_header_flag) {
      sbrHeader = this.sbr_header();
      num_sbr_bits += sbrHeader.nBits;
    }

    const sbrData = this.sbr_data(id_aac, sbrHeader?.bs_amp_res || 0);
    num_sbr_bits += sbrData.nBits;

    const num_align_bits = (8 * cnt - 4 - num_sbr_bits) % 8;
    this.bitReader.readBits(num_align_bits);

    return {
      n: ((num_sbr_bits + num_align_bits + 4) / 8),
      bs_sbr_crc_bits,
      num_sbr_bits,
      num_align_bits,
    };
  }


  private sbr_data(id_aac: number, bs_amp_res: number): SbrData {
    // Like ffmpeg, let's assume that we sbr_layer === SBR_NOT_SCALABLE
    switch (id_aac) {
      case ID_SCE: return this.sbr_single_channel_element(bs_amp_res);
      case ID_CPE: return this.sbr_channel_pair_element(bs_amp_res);
      default: throw new Error(`Unsupported id_aac ${id_aac} in sbr_data()`);
    }
  }


  private sbr_single_channel_element(bs_amp_res: number): SbrData {
    throw new Error('sbr_single_channel_element not implemented');
  }


  private sbr_channel_pair_element(bs_amp_res: number): SbrData {
    throw new Error('sbr_channel_pair_element not implemented');
  }


  private sbr_header(): SbrHeader {
    let nBits = 16;
    const bs_amp_res = this.bitReader.readBits(1);
    const bs_start_freq = this.bitReader.readBits(4);
    const bs_stop_freq = this.bitReader.readBits(4);
    const bs_xover_band = this.bitReader.readBits(3);
    let bs_freq_scale = 0;
    let bs_alter_scale = 0;
    let bs_noise_bands = 0;
    let bs_limiter_bands = 0;
    let bs_limiter_gains = 0;
    let bs_interpol_freq = 0;
    let bs_smoothing_mode = 0;

    // bs_reserved
    this.bitReader.readBits(2);

    const bs_header_extra_1 = this.bitReader.readBits(1);
    const bs_header_extra_2 = this.bitReader.readBits(1);

    if (bs_header_extra_1 === 1) {
      nBits += 5;
      bs_freq_scale = this.bitReader.readBits(2);
      bs_alter_scale = this.bitReader.readBits(1);
      bs_noise_bands = 0;
    }

    if (bs_header_extra_2 === 1) {
      nBits += 6;
      bs_limiter_bands = this.bitReader.readBits(2);
      bs_limiter_gains = this.bitReader.readBits(2);
      bs_interpol_freq = this.bitReader.readBits(1);
      bs_smoothing_mode = this.bitReader.readBits(1);
    }

    return {
      nBits,
      bs_amp_res,
      bs_start_freq,
      bs_stop_freq,
      bs_xover_band,
      bs_header_extra_1,
      bs_header_extra_2,
      bs_freq_scale,
      bs_alter_scale,
      bs_noise_bands,
      bs_limiter_bands,
      bs_limiter_gains,
      bs_interpol_freq,
      bs_smoothing_mode,
    };
  }


  private dynamic_range_info(): DynamicRangeInfo {
    let n = 1;
    let drc_num_bands = 1;

    let pce_instance_tag = 0;
    let drc_tag_reserved_bits = 0;
    let drc_band_incr = 0;
    let drc_bands_reserved_bits = 0;
    const drc_band_top: number[] = [];
    let prog_ref_level = 0;
    let prog_ref_level_reserved_bits = 0;
    const dyn_rng_sgn: number[] = [];
    const dyn_rng_ctl: number[] = [];

    const pce_tag_present = this.bitReader.readBits(1);
    if (pce_tag_present === 1) {
      pce_instance_tag = this.bitReader.readBits(4);
      drc_tag_reserved_bits = this.bitReader.readBits(4);
      n++;
    }

    const excluded_chns_present = this.bitReader.readBits(1);
    if (excluded_chns_present === 1) {
      n += this.excluded_channels().n;
    }

    const drc_bands_present = this.bitReader.readBits(1);
    if (drc_bands_present === 1) {
      drc_band_incr = this.bitReader.readBits(4);
      drc_bands_reserved_bits = this.bitReader.readBits(4);
      n++;
      drc_num_bands += drc_band_incr;
      for (let i = 0; i < drc_num_bands; i++) {
        drc_band_top[i] = this.bitReader.readBits(8);
        n++;
      }
    }

    const prog_ref_level_present = this.bitReader.readBits(1);
    if (prog_ref_level_present === 1) {
      prog_ref_level = this.bitReader.readBits(7);
      prog_ref_level_reserved_bits = this.bitReader.readBits(1);
      n++;
    }

    for (let i = 0; i < drc_num_bands; i++) {
      dyn_rng_sgn[i] = this.bitReader.readBits(1);
      dyn_rng_ctl[i] = this.bitReader.readBits(7);
      n++;
    }

    return {
      n,
      pce_tag_present,
      pce_instance_tag,
      drc_tag_reserved_bits,
      excluded_chns_present,
      drc_bands_present,
      drc_band_incr,
      drc_bands_reserved_bits,
      drc_band_top,
      prog_ref_level_present,
      prog_ref_level,
      prog_ref_level_reserved_bits,
      dyn_rng_sgn,
      dyn_rng_ctl,
    };
  }


  private excluded_channels(): ExcludeChannelsInfo {
    let n = 0;
    const exclude_mask: number[] = [];
    const additional_excluded_chns: number[] = [];

    let num_excl_chan = 70;

    for (let i = 0; i < 7; i++) {
      exclude_mask[i] = this.bitReader.readBits(1);
    }
    n++;

    while ((additional_excluded_chns[n - 1] = this.bitReader.readBits(1)) === 1) {
      for (let i = num_excl_chan; i < num_excl_chan + 7; i++) {
        exclude_mask[i] = this.bitReader.readBits(1);
      }
      n++;
      num_excl_chan += 7;
    }

    return {
      n,
      exclude_mask,
      additional_excluded_chns,
    };
  }


  private individual_channel_stream(
    common_window: number,
    sampling_frequency_index: number,
    ics_info: IcsInfo | undefined,
    scalefactorBandsAndGroupingInfo: ScalefactorBandsAndGroupingInfo | undefined,
    channelIndex: number,
  ): ChannelElement {
    let tns: TemporalNoiseShaping | undefined;
    const global_gain = this.bitReader.readBits(8);

    if (!common_window) {
      ics_info = this.ics_info(sampling_frequency_index, channelIndex);
      scalefactorBandsAndGroupingInfo = this.getScalefactorBandsAndGroupingInfo(sampling_frequency_index, ics_info);
    }

    invariant(ics_info !== undefined, 'Should have ics_info');
    invariant(scalefactorBandsAndGroupingInfo !== undefined, 'Should scalefactorBandsAndGroupingInfo ics_info');

    const section_data_info = this.section_data(ics_info, scalefactorBandsAndGroupingInfo);
    const scaleFactors = this.scale_factor_data(channelIndex, ics_info, scalefactorBandsAndGroupingInfo, section_data_info, global_gain);

    let pulse: Pulse | undefined;
    const pulse_data_present = this.bitReader.readBits(1);
    if (pulse_data_present) {
      throw 'pulse_data not implemented';
    }

    const tns_data_present = this.bitReader.readBits(1);
    if (tns_data_present) {
      tns = this.tns_data(ics_info, scalefactorBandsAndGroupingInfo);
    }

    const gain_control_data_present = this.bitReader.readBits(1);
    if (gain_control_data_present) {
      throw 'gain_control_data not implemented';
    }

    const coeffs = this.spectral_data(ics_info, scalefactorBandsAndGroupingInfo, scaleFactors, pulse, section_data_info.band_type, channelIndex);

    if (this.samples[channelIndex] === undefined) {
      this.samples[channelIndex] = new Float32Array(1024);
    }
    const samples = this.samples[channelIndex];

    if (this.overlaps[channelIndex] === undefined) {
      this.overlaps[channelIndex] = new Float32Array(1024);
    }
    const overlaps = this.overlaps[channelIndex];

    return {
      ics_info,
      scalefactorBandsAndGroupingInfo,
      coeffs,
      section_data_info,
      scaleFactors,
      tns,
      channelIndex,
      samples,
      overlaps,
    };
  }


  private tns_data(ics_info: IcsInfo, scalefactorBandsAndGroupingInfo: ScalefactorBandsAndGroupingInfo): TemporalNoiseShaping {
    const tns: TemporalNoiseShaping = {
      n_filt: [],
      length: [],
      direction: [],
      order: [],
      coef: [],
    };
    const is8 = (ics_info.current_window_sequence === EIGHT_SHORT_SEQUENCE) ? 1 : 0;
    // For the MAIN aac profile that would be 20, but we only support LC for now
    const tns_max_order = is8 === 1 ? 7 : 12;
    for (let w = 0; w < scalefactorBandsAndGroupingInfo.num_windows; w++) {
      tns.n_filt[w] = this.bitReader.readBits(2 - is8);
      tns.length[w] = [];
      tns.direction[w] = [];
      tns.order[w] = [];
      tns.coef[w] = [];

      if (tns.n_filt[w] !== 0) {
        const coef_res = this.bitReader.readBits(1);
        for (let filt = 0; filt < tns.n_filt[w]!; filt++) {
          tns.length[w]![filt] = this.bitReader.readBits(6 - 2 * is8);
          tns.order[w]![filt] = this.bitReader.readBits(5 - 2 * is8);
          tns.coef[w]![filt] = [];
          if (tns.order[w]![filt]! > tns_max_order) {
            throw `TNS filter order ${tns.order[w]![filt]} is greater than maximum ${tns_max_order}`;
          }
          if (tns.order[w]![filt]! > 0) {
            tns.direction[w]![filt] = this.bitReader.readBits(1);
            const coef_compress = this.bitReader.readBits(1);
            const coef_len = coef_res + 3 - coef_compress;
            const tmp2_idx = 2 * coef_compress + coef_res;
            for (let i = 0; i < tns.order[w]![filt]!; i++) {
              const index = this.bitReader.readBits(coef_len);
              // @ts-expect-error noUncheckedIndexedAccess - these index accesses are correct
              tns.coef[w][filt][i] = tns_tmp2_map[tmp2_idx][index];
            }
          }
        }
      }
    }

    return tns;
  }


  private spectral_data(
    ics_info: IcsInfo,
    scalefactorBandsAndGroupingInfo: ScalefactorBandsAndGroupingInfo,
    scaleFactors: Float32Array,
    pulse: Pulse | undefined,
    band_type: number[],
    channelIndex: number,
  ): Float32Array {
    if (this.coeffs[channelIndex] === undefined) {
      this.coeffs[channelIndex] = new Float32Array(1024);
    } else {
      this.coeffs[channelIndex].fill(0);
    }
    const data = this.coeffs[channelIndex];

    const offsets = scalefactorBandsAndGroupingInfo.swb_offset;
    let groupOff = 0;
    let idx = 0;
    for (let g = 0; g < scalefactorBandsAndGroupingInfo.num_window_groups; g++) {
      const groupLen = scalefactorBandsAndGroupingInfo.window_group_length[g]!;

      for (let sfb = 0; sfb < ics_info.max_sfb; sfb++, idx++) {
        const hcb = band_type[idx];
        let off = groupOff + offsets[sfb]!;
        const width = offsets[sfb + 1]! - offsets[sfb]!;

        if (hcb === ZERO_HCB || hcb === INTENSITY_HCB || hcb === INTENSITY_HCB2) {
          for (let group = 0; group < groupLen; group++, off += 128) {
            for (let i = off; i < off + width; i++) {
              data[i] = 0;
            }
          }
        } else if (hcb === NOISE_BAND_TYPE) {
          // fill with random values
          for (let group = 0; group < groupLen; group++, off += 128) {
            let energy = 0;

            for (let k = 0; k < width; k++) {
              this.randomState = (this.randomState * 1664525 + 1013904223) & 0xFFFFFFFF;
              data[off + k] = this.randomState;
              energy += data[off + k]! * data[off + k]!;
            }

            const scale = scaleFactors[idx]! / Math.sqrt(energy);
            for (let k = 0; k < width; k++) {
              data[off + k]! *= scale;
            }
          }
        } else {
          for (let group = 0; group < groupLen; group++, off += 128) {
            for (let k = 0; k < width;) {
              const buf = decodeSpectralData(this.bitReader, hcb!);

              // inverse quantization & scaling
              for (let j = 0; j < buf.length; j++) {
                data[off + k + j]! = (buf[j]! > 0) ? inverseQuantizationLookup(buf[j]!)! : -inverseQuantizationLookup(-buf[j]!)!;
                data[off + k + j]! *= scaleFactors[idx]!;
              }

              k += buf.length;
            }
          }
        }
      }

      groupOff += groupLen << 7;
    }

    if (pulse !== undefined) {
      throw 'pulse not implemented';
    }

    return data;
  }


  private scale_factor_data(
    channelIndex: number,
    ics_info: IcsInfo,
    scalefactorBandsAndGroupingInfo: ScalefactorBandsAndGroupingInfo,
    section_data_info: SectionDataInfo,
    global_gain: number,
  ): Float32Array {
    if (this.scaleFactors[channelIndex] === undefined) {
      this.scaleFactors[channelIndex] = new Float32Array(MAX_SCALE_FACTORS);
    }

    const scaleFactors = this.scaleFactors[channelIndex];
    let idx = 0;
    let noise_flag = true;
    const offset = [global_gain, global_gain - 90, 0]; // spectrum, noise, intensity

    for (let g = 0; g < scalefactorBandsAndGroupingInfo.num_window_groups; g++) {
      for (let i = 0; i < ics_info.max_sfb;) {
        const run_end = section_data_info.band_type_run_end[idx]!;

        switch (section_data_info.band_type[idx]) {
          case ZERO_HCB: {
            for (; i < run_end; i++, idx++) {
              scaleFactors[idx] = 0;
            }
            break;
          }

          case INTENSITY_HCB:
          case INTENSITY_HCB2: {
            for (; i < run_end; i++, idx++) {
              offset[2]! += decodeScaleFactor(this.bitReader);
              const clipped_offset = Math.min(Math.max(offset[2]!, -155), 100);
              scaleFactors[idx] = scaleFactorLookup(-clipped_offset + SF_OFFSET)!;
            }
            break;
          }

          case NOISE_BAND_TYPE: {
            for (; i < run_end; i++, idx++) {
              if (noise_flag) {
                offset[1]! += this.bitReader.readBits(NOISE_PRE_BITS) - NOISE_PRE;
                noise_flag = false;
              } else {
                offset[1]! += decodeScaleFactor(this.bitReader);
              }
              const clipped_offset = Math.min(Math.max(offset[1]!, -100), 155);
              scaleFactors[idx] = -scaleFactorLookup(clipped_offset + SF_OFFSET)!;
            }
            break;
          }

          default: {
            for (; i < run_end; i++, idx++) {
              const scaleFactorDiff = decodeScaleFactor(this.bitReader);
              offset[0]! += scaleFactorDiff;
              if (offset[0]! > 255) {
                throw new Error(`Offset ${offset[0]} out of range`);
              }
              scaleFactors[idx] = -scaleFactorLookup(offset[0]! - 100 + SF_OFFSET)!;
            }
            break;
          }
        }
      }
    }

    return scaleFactors;
  }


  private section_data(ics_info: IcsInfo, scalefactorBandsAndGroupingInfo: ScalefactorBandsAndGroupingInfo): SectionDataInfo {
    const nBits = ics_info.current_window_sequence == EIGHT_SHORT_SEQUENCE ? 3 : 5;
    const band_type: number[] = [];
    const band_type_run_end: number[] = [];

    let idx = 0;
    for (let g = 0; g < scalefactorBandsAndGroupingInfo.num_window_groups; g++) {
      let k = 0;
      while (k < ics_info.max_sfb) {
        let sect_end = k;
        let sect_len_incr: number;
        const sect_band_type = this.bitReader.readBits(4);
        do {
          sect_len_incr = this.bitReader.readBits(nBits);
          sect_end += sect_len_incr;
        } while (sect_len_incr == (1 << nBits) - 1);
        for (; k < sect_end; k++) {
          band_type[idx] = sect_band_type;
          band_type_run_end[idx++] = sect_end;
        }
      }
    }
    return {
      band_type,
      band_type_run_end,
    };
  }


  private ics_info(sampling_frequency_index: number, channelIndex: number): IcsInfo {
    // ics_reserved_bit
    this.bitReader.readBits(1);

    const current_window_sequence = this.bitReader.readBits(2);
    const current_window_shape = this.bitReader.readBits(1);

    this.previous_window_sequence[channelIndex] = this.current_window_sequence[channelIndex] ?? 0;
    this.current_window_sequence[channelIndex] = current_window_sequence;
    this.previous_window_shape[channelIndex] = this.current_window_shape[channelIndex] ?? 0;
    this.current_window_shape[channelIndex] = current_window_shape;

    let max_sfb: number;
    let scale_factor_grouping = 0;
    let predictor_data_present = 0;
    let predictor_reset = 0;
    let predictor_reset_group_number = 0;
    const prediction_used: number[] = [];

    if (current_window_sequence === EIGHT_SHORT_SEQUENCE) {
      max_sfb = this.bitReader.readBits(4);
      scale_factor_grouping = this.bitReader.readBits(7);
    } else {
      max_sfb = this.bitReader.readBits(6);
      predictor_data_present = this.bitReader.readBits(1);
      if (predictor_data_present) {
        predictor_reset = this.bitReader.readBits(1);
        if (predictor_reset) {
          predictor_reset_group_number = this.bitReader.readBits(5);
        }

        const max = Math.min(max_sfb, PRED_SFB_MAX[sampling_frequency_index]!);
        for (let sfb = 0; sfb < max; sfb++) {
          prediction_used[sfb] = this.bitReader.readBits(1);
        }
      }
    }

    return {
      previous_window_sequence: this.previous_window_sequence[channelIndex],
      current_window_sequence,
      previous_window_shape: this.previous_window_shape[channelIndex],
      current_window_shape,
      max_sfb,
      scale_factor_grouping,
      predictor_data_present,
      predictor_reset,
      predictor_reset_group_number,
      prediction_used,
    };
  }


  private getScalefactorBandsAndGroupingInfo(
    sampling_frequency_index: number,
    ics_info: IcsInfo,
  ): ScalefactorBandsAndGroupingInfo {
    let num_windows = 0;
    let num_window_groups = 0;
    const window_group_length: number[] = [];
    let num_swb = 0;
    const sect_sfb_offset: number[][] = [];
    const swb_offset: number[] = [];
    let tns_max_bands = 0;

    const fs_index = sampling_frequency_index;
    switch (ics_info.current_window_sequence) {
      case ONLY_LONG_SEQUENCE:
      case LONG_START_SEQUENCE:
      case LONG_STOP_SEQUENCE: {
        num_windows = 1;
        num_window_groups = 1;
        window_group_length[num_window_groups - 1] = 1;
        num_swb = num_swb_long_window[fs_index]!;
        tns_max_bands = tns_max_bands_1024[fs_index]!;
        sect_sfb_offset[0] = [];
        /* preparation of sect_sfb_offset for long blocks */
        /* also copy the last value! */
        for (let i = 0; i < ics_info.max_sfb + 1; i++) {
          sect_sfb_offset[0][i] = swb_offset_long_window[fs_index]![i]!;
          swb_offset[i] = swb_offset_long_window[fs_index]![i]!;
        }
        break;
      }

      case EIGHT_SHORT_SEQUENCE: {
        num_windows = 8;
        num_window_groups = 1;
        window_group_length[num_window_groups - 1] = 1;
        num_swb = num_swb_short_window[fs_index]!;
        tns_max_bands = tns_max_bands_128[fs_index]!;
        for (let i = 0; i < num_swb_short_window[fs_index]! + 1; i++) {
          swb_offset[i] = swb_offset_short_window[fs_index]![i]!;
        }
        for (let i = 0; i < num_windows - 1; i++) {
          if ((ics_info.scale_factor_grouping & 1 << (6 - i)) === 0) {
            num_window_groups += 1;
            window_group_length[num_window_groups - 1] = 1;
          } else {
            window_group_length[num_window_groups - 1]! += 1;
          }
        }
        /* preparation of sect_sfb_offset for short blocks */
        for (let g = 0; g < num_window_groups; g++) {
          let sect_sfb = 0;
          let offset = 0;
          sect_sfb_offset[g] = [];
          for (let i = 0; i < ics_info.max_sfb; i++) {
            let width = swb_offset_short_window[fs_index]![i + 1]! - swb_offset_short_window[fs_index]![i]!;
            width *= window_group_length[g]!;
            sect_sfb_offset[g]![sect_sfb++] = offset;
            offset += width;
          }
          sect_sfb_offset[g]![sect_sfb] = offset;
        }

        break;
      }
    }

    return {
      num_windows,
      num_window_groups,
      window_group_length,
      num_swb,
      sect_sfb_offset,
      swb_offset,
      tns_max_bands,
    };
  }


  public clearPcmSamples(emitPCMs16LESamples: boolean) {
    this.channels = [];
    this.emitPCMs16LESamples = emitPCMs16LESamples;
  }


  public getChannels(): Float32Array[][] {
    return this.channels;
  }


  public getSamples(): Uint8Array {
    return this.bitWriter.getBuffer();
  }

}
